// src/components/modals/ShareModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Link2, Code2, Check, Copy, Globe, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { apiCall } from '../../utils/api';

export default function ShareModal({ isOpen, onClose, chartId, chartTitle, visibility }) {
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  useEffect(() => {
    if (isOpen && chartId) {
      loadShareData();
    }
  }, [isOpen, chartId]);

  async function loadShareData() {
    setLoading(true);
    try {
      const res = await apiCall(`/charts/${chartId}/share-link`);
      if (res.ok) {
        const data = await res.json();
        setShareData(data);
      }
    } catch (err) {
      console.error('Failed to load share data:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleCopyLink = async () => {
    if (!shareData) return;
    
    try {
      await navigator.clipboard.writeText(shareData.share_url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCopyEmbed = async () => {
    if (!shareData) return;
    
    try {
      await navigator.clipboard.writeText(shareData.embed_code);
      setCopiedEmbed(true);
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (err) {
      console.error('Failed to copy embed code:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-2xl max-w-2xl w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#2a2a2a]">
            <div>
              <h2 className="text-xl font-semibold text-white">Share Chart</h2>
              <p className="text-sm text-[#6a6a6a] mt-1">{chartTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-[#a0a0a0]" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Visibility Status */}
            <div className="flex items-center justify-between p-4 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg">
              <div className="flex items-center gap-3">
                {visibility === 'public' ? (
                  <>
                    <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Public Chart</p>
                      <p className="text-xs text-[#6a6a6a]">Anyone with the link can view</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-full bg-[#2a2a2a] flex items-center justify-center">
                      <Lock className="w-5 h-5 text-[#6a6a6a]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Private Chart</p>
                      <p className="text-xs text-[#6a6a6a]">Only you can view this chart</p>
                    </div>
                  </>
                )}
              </div>
              <Badge variant={visibility === 'public' ? 'success' : 'neutral'}>
                {visibility === 'public' ? 'Public' : 'Private'}
              </Badge>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : shareData ? (
              <>
                {/* Share Link */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Link2 className="w-4 h-4 text-blue-500" />
                    <h3 className="text-sm font-medium text-white">Share Link</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareData.share_url}
                      readOnly
                      className="flex-1 h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 select-all"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      variant="secondary"
                      onClick={handleCopyLink}
                      className="min-w-[100px]"
                    >
                      {copiedLink ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-[#6a6a6a] mt-2">
                    Share this link to give others direct access to the chart
                  </p>
                </div>

                {/* Embed Code */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Code2 className="w-4 h-4 text-blue-500" />
                    <h3 className="text-sm font-medium text-white">Embed Code</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="relative">
                      <pre className="p-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-xs text-[#a0a0a0] overflow-x-auto">
                        <code>{shareData.embed_code}</code>
                      </pre>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCopyEmbed}
                        className="absolute top-2 right-2"
                      >
                        {copiedEmbed ? (
                          <>
                            <Check className="w-3 h-3" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-[#6a6a6a]">
                      Paste this code into your website to embed the chart
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-red-400">Failed to load share links</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a2a]">
            <Button onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}